import { Check, FileText, BarChart3, Scale, Gavel, Loader2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { usePlaybackStage } from "@/lib/playback";

const STAGES = [
  { key: "intake", label: "Intake", Icon: FileText },
  { key: "analysis", label: "Analysis", Icon: BarChart3 },
  { key: "coordination", label: "Coordination", Icon: Scale },
  { key: "decision", label: "Decision", Icon: Gavel },
] as const;

export type StageKey = (typeof STAGES)[number]["key"];
const STAGE_KEYS: StageKey[] = ["intake", "analysis", "coordination", "decision"];

type StepRoute = "/apps/$id/intake" | "/apps/$id/analysis" | "/apps/$id/coordination" | "/apps/$id/decision";

export function StageStepper({
  applicationId,
  active,
  reached,
}: {
  applicationId: string;
  active: StageKey;
  reached: StageKey;
}) {
  const { playingStage } = usePlaybackStage();
  const reachedIdx = STAGE_KEYS.indexOf(reached);
  return (
    <ol className="flex items-center gap-2">
      {STAGES.map((s, idx) => {
        const isActive = s.key === active;
        const isPast = idx < reachedIdx && s.key !== active;
        const isFuture = idx > reachedIdx;
        const isPlaying = playingStage === s.key;
        const routeTo = stepRoute(s.key);
        return (
          <li key={s.key} className="flex items-center gap-2 flex-1 last:flex-initial">
            <Link
              to={routeTo}
              params={{ id: applicationId }}
              className="group flex flex-col items-center focus:outline-none"
              aria-current={isActive ? "step" : undefined}
            >
              <div
                className={cn(
                  "relative flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors",
                  "group-hover:ring-2 group-hover:ring-ring/40 group-focus-visible:ring-2 group-focus-visible:ring-ring",
                  isPast && !isPlaying && "border-success bg-success text-success-foreground",
                  isActive && !isPlaying && "border-warn bg-warn/10 text-warn",
                  !isPast && !isActive && !isPlaying && "border-border bg-card text-muted-foreground",
                  isPlaying && "border-warn bg-warn/10 text-warn animate-pulse",
                )}
              >
                {isPlaying ? (
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.2} />
                ) : isPast ? (
                  <Check className="h-4 w-4" strokeWidth={3} />
                ) : (
                  <s.Icon className="h-4 w-4" strokeWidth={1.9} />
                )}
              </div>
              <div
                className={cn(
                  "mt-1 text-[0.72rem] font-medium transition-colors",
                  isPast && !isPlaying && "text-success",
                  (isActive || isPlaying) && "text-warn",
                  !isPast && !isActive && !isPlaying && "text-muted-foreground group-hover:text-foreground",
                  isFuture && "italic",
                )}
              >
                {s.label}
              </div>
            </Link>
            {idx < STAGES.length - 1 && (
              <div className={cn("stage-stepper-line", idx < reachedIdx && "stage-stepper-line--done")} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function stepRoute(k: StageKey): StepRoute {
  switch (k) {
    case "intake":
      return "/apps/$id/intake";
    case "analysis":
      return "/apps/$id/analysis";
    case "coordination":
      return "/apps/$id/coordination";
    case "decision":
      return "/apps/$id/decision";
  }
}
